import { Reveal, Eyebrow } from "./Reveal";
import { FRAMEWORK, DIFFERENCE, IMAGES } from "../data/content";

const ApproachIntro = () => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
    <Reveal className="lg:col-span-5">
      <Eyebrow>Our Approach — The 6D Framework</Eyebrow>
      <h2 className="f-display text-4xl sm:text-5xl lg:text-[3.6rem] text-[#0B2A5B] tracking-tight mt-4 max-w-3xl leading-[1.1]">
        We don&rsquo;t just deliver reports.{" "}
        <span className="italic text-[#1E5FE0]">We walk the journey with you&mdash;from insight to impact.</span>
      </h2>
      <p className="mt-6 max-w-2xl text-base text-slate-600 leading-relaxed">
        Most consulting ends at recommendation. Ours begins there. The 6D
        Framework keeps us alongside your team from diagnosis through deployment
        and capability development — until impact is delivered and sustained.
      </p>
    </Reveal>
    <Reveal delay={0.1} className="lg:col-span-7">
      <div className="relative rounded-[2rem] overflow-hidden aspect-[16/7] bg-[#0B2A5B] shadow-[0_30px_60px_-30px_rgba(11,42,91,0.4)]">
        <img
          src={IMAGES.metamorphosis}
          alt="Transformation, stage by stage"
          data-testid="approach-image"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
      <p className="mt-4 f-label text-xs uppercase tracking-[0.2em] text-[#0B2A5B]/50 text-right">
        Transformation, stage by stage
      </p>
    </Reveal>
  </div>
);

const FrameworkCard = ({ f }) => (
  <div
    data-testid={`framework-card-${f.step.toLowerCase()}`}
    className="group h-full bg-white rounded-2xl border border-[#0B2A5B]/10 p-8 hover:border-[#1E5FE0]/40 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-24px_rgba(11,42,91,0.3)] transition-all duration-500"
  >
    <div className="flex items-baseline justify-between">
      <span className="f-label text-sm text-[#1E5FE0]">{f.step}</span>
      <span className="w-8 h-px bg-[#0B2A5B]/15 group-hover:bg-[#1E5FE0] group-hover:w-12 transition-all duration-500" />
    </div>
    <h3 className="f-display text-2xl text-[#0B2A5B] mt-5">{f.name}</h3>
    <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.desc}</p>
    <div className="mt-6 flex flex-wrap gap-2">
      {f.tags.map((t) => (
        <span key={t} className="f-label text-xs uppercase tracking-[0.12em] text-[#0B2A5B]/60 border border-[#0B2A5B]/15 rounded-full px-3 py-1">
          {t}
        </span>
      ))}
    </div>
  </div>
);

const Difference = () => (
  <div className="mt-24">
    <Reveal>
      <Eyebrow>Our Difference</Eyebrow>
      <h3 className="f-display text-2xl sm:text-3xl text-[#0B2A5B] mt-4">
        Recommendations tell you what to do. <span className="italic text-[#1E5FE0]">We help you do it.</span>
      </h3>
    </Reveal>
    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-10">
      {DIFFERENCE.map((d, i) => (
        <Reveal key={d.title} delay={i * 0.1} className="border-l-2 border-[#1E5FE0]/30 pl-6 hover:border-[#1E5FE0] transition-colors duration-500">
          <h4 className="f-display text-xl text-[#0B2A5B]">{d.title}</h4>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">{d.text}</p>
        </Reveal>
      ))}
    </div>
  </div>
);

export const Approach = () => (
  <section id="approach" data-testid="approach-section" className="py-24 lg:py-36 bg-[#F4F2EA] border-y border-[#0B2A5B]/10">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <ApproachIntro />
      <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {FRAMEWORK.map((f, i) => (
          <Reveal key={f.step} delay={i * 0.07}>
            <FrameworkCard f={f} />
          </Reveal>
        ))}
      </div>
      <Difference />
    </div>
  </section>
);
